if (!customElements.get('p80-drawer')) {
  customElements.define(
    'p80-drawer',
    class P80Drawer extends HTMLElement {
      constructor() {
        super();

        this.handleClick = this.onClick.bind(this);
        this.handleKeyUp = this.onKeyUp.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.handleTransitionEnd = this.onTransitionEnd.bind(this);
      }

      connectedCallback() {
        const tasks = [
          this.setConfiguration.bind(this),
          this.setAccessibilityOptions.bind(this),
          this.bindEvents.bind(this)
        ];

        this.yieldTasks(tasks);
      }

      disconnectedCallback() {
        this.unbindEvents();
        this.clearCloseFallback();
      }

      yieldToMain() {
        return new Promise((resolve) => {
          setTimeout(resolve, 0);
        });
      }

      async yieldTasks(tasks) {
        while (tasks.length > 0 && this.isConnected) {
          const task = tasks.shift();
          task();
          await this.yieldToMain();
        }
      }

      setConfiguration() {
        this.panel = this.querySelector('.p80-drawer__panel');
        this.overlay = this.querySelector('.p80-drawer__overlay');

        if (!this.panel) return;

        if (!this.hasAttribute('aria-hidden')) {
          this.setAttribute('aria-hidden', 'true');
        }

        this.panel.setAttribute('tabindex', this.panel.getAttribute('tabindex') || '-1');
        this.panel.setAttribute('role', this.panel.getAttribute('role') || 'dialog');
        this.panel.setAttribute('aria-modal', this.panel.getAttribute('aria-modal') || 'true');

        this.copyAriaAttributeToPanel('aria-label');
        this.copyAriaAttributeToPanel('aria-labelledby');
        this.copyAriaAttributeToPanel('aria-describedby');

        this.setOpenersAccessibility();
      }

      copyAriaAttributeToPanel(attribute) {
        if (!this.hasAttribute(attribute) || this.panel.hasAttribute(attribute)) return;
        this.panel.setAttribute(attribute, this.getAttribute(attribute));
      }

      bindEvents() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keyup', this.handleKeyUp);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeyDown);
      }

      unbindEvents() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keyup', this.handleKeyUp);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        this.panel?.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      setOpenersAccessibility() {
        this.openers.forEach((opener) => {
          opener.setAttribute('aria-haspopup', 'dialog');
          opener.setAttribute('aria-expanded', 'false');

          if (this.id) {
            opener.setAttribute('aria-controls', this.id);
          }

          if (!this.isNativeButton(opener) && !opener.hasAttribute('role')) {
            opener.setAttribute('role', 'button');
          }
        });
      }

      setAccessibilityOptions(isOpen = this.isOpen()) {
        this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        this.openers.forEach((opener) => {
          opener.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }

      onClick(event) {
        const closeControl = event.target.closest('[data-p80-drawer-close]');
        const clickedOverlay = this.overlay && event.target === this.overlay;

        if (!closeControl && !clickedOverlay) return;

        event.preventDefault();
        this.close();
      }

      onKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE' || !this.isOpen()) return;
        event.preventDefault();
        this.close();
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-p80-drawer-open]');
        if (!this.openerTargetsDrawer(opener)) return;

        event.preventDefault();
        this.open(opener);
      }

      onDocumentKeyDown(event) {
        if (!['ENTER', 'SPACE'].includes(event.code.toUpperCase())) return;

        const opener = event.target.closest('[data-p80-drawer-open]');
        if (!this.openerTargetsDrawer(opener) || this.isNativeButton(opener)) return;

        event.preventDefault();
        this.open(opener);
      }

      openerTargetsDrawer(opener) {
        if (!opener) return false;

        const target =
          opener.getAttribute('data-p80-drawer-open') || opener.getAttribute('aria-controls');
        if (!target) return false;

        if (this.id && (target === this.id || target === `#${this.id}`)) return true;

        try {
          return document.querySelector(target) === this;
        } catch {
          return false;
        }
      }

      open(opener = null) {
        if (!this.panel || this.isOpen()) return;

        this.closeOtherDrawers();
        this.openedBy = opener || document.activeElement;
        this.setAttribute('open', '');
        this.clearCloseFallback();
        if (this.shouldMoveToBody()) this.moveToBody();

        window.requestAnimationFrame(() => {
          this.classList.add('p80-drawer--visible');
          this.setAccessibilityOptions();
          document.body.classList.add('overflow-hidden');
          trapFocus(this.panel, this.getInitialFocusElement());
          this.dispatchDrawerEvent('open');
        });
      }

      close(returnFocus = true) {
        if (!this.isOpen()) return;

        this.classList.remove('p80-drawer--visible');
        document.body.classList.remove('overflow-hidden');
        this.setAccessibilityOptions(false);
        removeTrapFocus(returnFocus ? this.openedBy : null);
        this.panel?.addEventListener('transitionend', this.handleTransitionEnd);
        this.closeFallback = setTimeout(this.handleTransitionEnd, 500);
        this.dispatchDrawerEvent('close');
      }

      onTransitionEnd(event) {
        if (event && event.target !== this.panel) return;

        this.panel?.removeEventListener('transitionend', this.handleTransitionEnd);
        this.clearCloseFallback();

        if (this.classList.contains('p80-drawer--visible')) return;

        this.removeAttribute('open');
        this.openedBy = null;
        this.restoreOriginalPosition();
      }

      clearCloseFallback() {
        if (!this.closeFallback) return;
        clearTimeout(this.closeFallback);
        this.closeFallback = null;
      }

      closeOtherDrawers() {
        document.querySelectorAll('p80-drawer[open]').forEach((drawer) => {
          if (drawer !== this && typeof drawer.close === 'function') {
            drawer.close(false);
          }
        });
      }

      moveToBody() {
        if (this.parentElement === document.body) return;

        if (!this.locationPlaceholder && this.parentNode) {
          this.locationPlaceholder = document.createComment(
            `p80-drawer-placeholder${this.id ? `-${this.id}` : ''}`
          );
          this.parentNode.insertBefore(this.locationPlaceholder, this);
        }

        document.body.appendChild(this);
      }

      shouldMoveToBody() {
        const moveToBody = this.getAttribute('data-move-to-body');
        return moveToBody === '' || moveToBody === 'true';
      }

      restoreOriginalPosition() {
        if (!this.locationPlaceholder) return;

        if (!this.locationPlaceholder.parentNode) {
          this.remove();
          this.locationPlaceholder = null;
          return;
        }

        this.locationPlaceholder.parentNode.insertBefore(this, this.locationPlaceholder);
        this.locationPlaceholder.remove();
        this.locationPlaceholder = null;
      }

      getInitialFocusElement() {
        return (
          this.querySelector('[data-p80-drawer-initial-focus]') ||
          this.panel.querySelector('[autofocus]') ||
          getFocusableElements(this.panel)[0] ||
          this.panel
        );
      }

      isOpen() {
        return this.hasAttribute('open');
      }

      isNativeButton(element) {
        return ['BUTTON', 'SUMMARY'].includes(element?.tagName);
      }

      dispatchDrawerEvent(action) {
        this.dispatchEvent(
          new CustomEvent(`p80:drawer:${action}`, {
            bubbles: true,
            detail: {
              drawer: this,
              opener: this.openedBy
            }
          })
        );
      }

      get openers() {
        return Array.from(document.querySelectorAll('[data-p80-drawer-open]')).filter((opener) =>
          this.openerTargetsDrawer(opener)
        );
      }
    }
  );
}
